import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  MessageSquare, Phone, Mail, Play, Pause, Send,
  CheckCircle2, XCircle, Clock, Loader2, Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICON = {
  send_sms: MessageSquare, sms: MessageSquare,
  send_email: Mail, email: Mail,
  make_call: Phone, call: Phone,
  notify_agent: Zap,
};
const ACTION_COLOR = {
  send_sms: 'text-blue-400', sms: 'text-blue-400',
  send_email: 'text-amber-400', email: 'text-amber-400',
  make_call: 'text-emerald-400', call: 'text-emerald-400',
  notify_agent: 'text-violet-400',
};
const STATUS_STYLE = {
  done: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  sent: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  queued: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  pending: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-secondary' },
  failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
  mock_sent: { icon: CheckCircle2, color: 'text-muted-foreground', bg: 'bg-secondary' },
  skipped: { icon: XCircle, color: 'text-muted-foreground', bg: 'bg-secondary' },
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = d - now;
  if (diff > 0) {
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `in ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `in ${days}d`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function FollowUpPanel({ leadId, leadName }) {
  const { token } = useAuth();
  const [sequences, setSequences] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!token || !leadId) return;
    try {
      const { data } = await axios.get(`${API}/follow-ups/lead/${leadId}`, { headers });
      setSequences(data.sequences || []);
      setLogs(data.logs || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [token, leadId]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  const createSequence = async (type) => {
    setCreating(true);
    try {
      await axios.post(`${API}/follow-ups/create`, { lead_id: leadId, sequence_type: type }, { headers });
      toast.success(`${type} sequence created`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create sequence');
    }
    setCreating(false);
  };

  const toggleSequence = async (seqId, status) => {
    try {
      const action = status === 'active' ? 'pause' : 'resume';
      await axios.post(`${API}/follow-ups/${seqId}/${action}`, {}, { headers });
      toast.success(`Sequence ${action}d`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const manualFollowUp = async () => {
    setTriggering(true);
    try {
      const { data } = await axios.post(`${API}/follow-ups/manual/${leadId}`, {}, { headers });
      toast.success(`Follow-up ${data.action} sent`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
    setTriggering(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-muted-foreground" size={20} /></div>;
  }

  const activeSeq = sequences.find(s => s.status === 'active' || s.status === 'paused');

  return (
    <div className="space-y-4" data-testid="followup-panel">
      {/* Actions Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={manualFollowUp}
          disabled={triggering}
          data-testid="manual-followup-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {triggering ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Follow Up Now
        </button>

        {!activeSeq && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Start sequence:</span>
            {['hot', 'warm', 'cold'].map(t => (
              <button
                key={t}
                onClick={() => createSequence(t)}
                disabled={creating}
                data-testid={`create-seq-${t}`}
                className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors border ${
                  t === 'hot' ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' :
                  t === 'warm' ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10' :
                  'border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active Sequence */}
      {activeSeq && (
        <div className="border border-border rounded-lg overflow-hidden" data-testid="active-sequence">
          <div className="flex items-center justify-between px-3 py-2 bg-card/80 border-b border-border">
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] ${
                activeSeq.sequence_type === 'hot' ? 'bg-red-500/15 text-red-400' :
                activeSeq.sequence_type === 'warm' ? 'bg-amber-500/15 text-amber-400' :
                'bg-cyan-500/15 text-cyan-400'
              }`}>
                {activeSeq.sequence_type}
              </Badge>
              <span className="text-xs text-foreground font-medium">Sequence</span>
              <Badge className={`text-[10px] ${
                activeSeq.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
              }`}>
                {activeSeq.status}
              </Badge>
            </div>
            <button
              onClick={() => toggleSequence(activeSeq.id, activeSeq.status)}
              data-testid="toggle-sequence-btn"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {activeSeq.status === 'active' ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
            </button>
          </div>

          <div className="divide-y divide-slate-800/50">
            {(activeSeq.steps || []).map((step, i) => {
              const Icon = ACTION_ICON[step.action] || MessageSquare;
              const ss = STATUS_STYLE[step.status] || STATUS_STYLE.pending;
              const StatusIcon = ss.icon;
              return (
                <div key={i} className="flex items-center gap-3 px-3 py-2" data-testid={`seq-step-${i}`}>
                  <div className={`shrink-0 p-1 rounded ${ss.bg}`}>
                    <Icon size={13} className={ACTION_COLOR[step.action] || 'text-muted-foreground'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">{step.label}</p>
                    {step.message && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{step.message}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <StatusIcon size={12} className={ss.color} />
                    <span className="text-[10px] text-muted-foreground">
                      {step.status === 'pending' ? formatTime(step.scheduled_at) : step.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stopped/Completed Sequences */}
      {sequences.filter(s => s.status !== 'active' && s.status !== 'paused').length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Past Sequences</h4>
          {sequences.filter(s => s.status !== 'active' && s.status !== 'paused').map(seq => (
            <div key={seq.id} className="flex items-center gap-2 py-1.5 text-xs text-muted-foreground" data-testid={`past-seq-${seq.id}`}>
              <Badge className="text-[10px] bg-secondary text-muted-foreground">{seq.sequence_type}</Badge>
              <span>{seq.status}{seq.stop_reason ? ` (${seq.stop_reason})` : ''}</span>
              <span className="ml-auto">{new Date(seq.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Follow-Up Activity Log */}
      {logs.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Activity Log</h4>
          <ScrollArea className="max-h-48">
            <div className="space-y-1">
              {logs.map((log, i) => {
                const Icon = ACTION_ICON[log.action] || MessageSquare;
                const ss = STATUS_STYLE[log.status] || STATUS_STYLE.pending;
                return (
                  <div key={log.id || i} className="flex items-start gap-2 py-1.5" data-testid={`followup-log-${i}`}>
                    <Icon size={12} className={`mt-0.5 ${ACTION_COLOR[log.action] || 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">{log.content || log.subject || log.action}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(log.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {' '}
                        <span className={ss.color}>{log.status}</span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Empty State */}
      {!activeSeq && logs.length === 0 && (
        <div className="text-center py-6 text-muted-foreground" data-testid="followup-empty">
          <MessageSquare size={24} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No follow-ups yet</p>
          <p className="text-xs mt-1">Start a sequence or send a manual follow-up</p>
        </div>
      )}
    </div>
  );
}
